import { useState, useEffect } from 'react';
import {
  Box,
  MenuItem,
  Stack,
  Typography,
  Select,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import {
  Wrapper,
} from "../../components";
import { PlatformDashboardContent } from "../../components/dashboard";
import { Calender } from "../../components";

import {
  Logs,
  apiUsageData,
  creditConsumptionData,
  platformHealthdata,
  apiCactegoryUsagedata,
  topCustomersdata,
  platformDashboardSummary,
  customerGrothData,
} from "../../services";

export function PlatformDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;

  /* ===================== STATE ===================== */
  const [dashboardSummaryData, setDashboardSummaryData] = useState(null);
  const [dashboardApiUsageData, setDashboardApiUsageData] = useState(null);
  const [dashboardCustomerGrothData, setDashboardCustomerGrothData] = useState(null);
  const [dashboardCreditConsumptionData, setDashboardCreditConsumptionData] = useState(null);
  const [dashboardPlatformHealthdata, setDashboardPlatformHealthdata] = useState(null);
  const [dashboardApiCactegoryUsagedata, setDashboardApiCactegoryUsagedata] = useState(null);

  /* ===== Date Filter State ===== */
  const [days, setDays] = useState("7");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);

  const currentDate = dayjs();
  const sevenDaysBack = currentDate.subtract(7, "day");

  const defaultDateRange = [
    sevenDaysBack.startOf("day"),
    currentDate.endOf("day"),
  ];

  const [dateRangeValue, setDateRangeValue] = useState(defaultDateRange);
  const [dashboardDates, setDashboardDates] = useState({
    startDate: defaultDateRange[0].format("YYYY-MM-DD"),
    endDate: defaultDateRange[1].format("YYYY-MM-DD"),
  });

  /* ===================== API CALLS ===================== */
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const [customers, setCustomers] = useState([]);
  const [totalCustomersCount, setTotalCustomersCount] = useState(0);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [growthLoading, setGrowthLoading] = useState(true);
  const [apiUsageLoading, setApiUsageLoading] = useState(true);
  const [creditLoading, setCreditLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  useEffect(() => {
    if (!jwtToken || !dashboardDates) return;

    setSummaryLoading(true);
    setGrowthLoading(true);
    setApiUsageLoading(true);
    setCreditLoading(true);
    setHealthLoading(true);
    setCategoryLoading(true);

    platformDashboardSummary(jwtToken, dashboardDates)
      .then((res) => res?.status === 200 && setDashboardSummaryData(res.data))
      .catch((e) => console.error("Summary API error:", e))
      .finally(() => setSummaryLoading(false));

    customerGrothData(jwtToken, dashboardDates)
      .then((res) => res?.status === 200 && setDashboardCustomerGrothData(res.data))
      .catch((e) => console.error("Growth API error:", e))
      .finally(() => setGrowthLoading(false));

    apiUsageData(jwtToken, dashboardDates)
      .then((res) => res?.status === 200 && setDashboardApiUsageData(res.data))
      .catch((e) => console.error("API usage error:", e))
      .finally(() => setApiUsageLoading(false));

    creditConsumptionData(jwtToken)
      .then((res) => res?.status === 200 && setDashboardCreditConsumptionData(res.data))
      .catch((e) => console.error("Credit API error:", e))
      .finally(() => setCreditLoading(false));

    platformHealthdata(jwtToken, dashboardDates)
      .then((res) => res?.status === 200 && setDashboardPlatformHealthdata(res.data))
      .catch((e) => console.error("Health API error:", e))
      .finally(() => setHealthLoading(false));

    apiCactegoryUsagedata(jwtToken, dashboardDates)
      .then((res) => res?.status === 200 && setDashboardApiCactegoryUsagedata(res.data))
      .catch((e) => console.error("Category API error:", e))
      .finally(() => setCategoryLoading(false));

  }, [jwtToken, dashboardDates]);
  

  useEffect(() => {
    if (!jwtToken || !dashboardDates) return;
  
    const fetchTopCustomers = async () => {
      try {
        setTableLoading(true);
  
        const res = await topCustomersdata(jwtToken, page, rowsPerPage, undefined, undefined, dashboardDates);
  
        if (res?.status === 200) {
          setCustomers(res.data?.data || []);
          setTotalCustomersCount(
            res.data?.pagination?.totalCompanies || 0
          );
        }
      } catch (error) {
        console.error("Top customers API error:", error);
      } finally {
        setTableLoading(false);
      }
    };
  
    fetchTopCustomers();
  
  }, [jwtToken, page, dashboardDates]);
  

  /* ===================== DATE HANDLERS ===================== */
  const handleDaysChange = (value) => {
    setDays(value);

    if (value === "custom") {
      setCalendarKey((prev) => prev + 1);
      setCalendarOpen(true);
      return;
    }

    setCalendarOpen(false);

    const endDate = dayjs();
    const startDate = endDate.subtract(Number(value), "day");
    const newRange = [startDate.startOf("day"), endDate.endOf("day")];

    setDateRangeValue(newRange);
    setDashboardDates({
      startDate: newRange[0].format("YYYY-MM-DD"),
      endDate: newRange[1].format("YYYY-MM-DD"),
    });
  };

  const handleChangeCalender = (dates) => {
    if (!dates) return;

    setDateRangeValue(dates);
    setDashboardDates({
      startDate: dates[0].format("YYYY-MM-DD"),
      endDate: dates[1].format("YYYY-MM-DD"),
    });
  };

  /* ===================== UI ===================== */
  return (
    <Wrapper>
      <Stack sx={{ pt: 2, mt: 7 }} spacing={2} className="wrapper">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            "@media (max-width: 576px)": {
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1,
            },
          }}
        >
          <Typography color="#424242" sx={{ fontWeight: 400, fontSize: "1.4rem" }}>
            Monitor all customers, API usage, and platform performance
          </Typography>
          {/* Date Filter */}
          <Box sx={{ ml: "auto", display: "flex", gap: 1,
              "@media (max-width: 576px)": {
                ml: "inherit",
              }
           }}>
            <Select
              size="small"
              value={days}
              onChange={(e) => handleDaysChange(e.target.value)}
              sx={{
                borderRadius: 3,
                fontSize: "14px",
                height: "38px",
                bgcolor: "#fff",
              }}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>

            {calendarOpen && (
              <Box key={calendarKey}>
                <Calender
                  handleChange={handleChangeCalender}
                  defaultValue={defaultDateRange}
                  value={dateRangeValue}
                  maxDate={new Date()}
                />
              </Box>
            )}
          </Box>
        </Box>

        <PlatformDashboardContent
          summaryLoading={summaryLoading}
          growthLoading={growthLoading}
          apiUsageLoading={apiUsageLoading}
          creditLoading={creditLoading}
          healthLoading={healthLoading}
          categoryLoading={categoryLoading}
          tableLoading={tableLoading}
          totalCustomers={dashboardSummaryData?.totalCustomers || 0}
          activeCustomers={dashboardSummaryData?.activeCustomers || 0}
          totalApiCalls={dashboardSummaryData?.totalApiCalls || 0}
          totalCreditConsumed={dashboardSummaryData?.totalCreditConsumed || 0}
          dashboardApiUsageData={dashboardApiUsageData}
          dashboardCustomerGrothData={dashboardCustomerGrothData}
          dashboardCreditConsumptionData={dashboardCreditConsumptionData}
          dashboardPlatformHealthdata={dashboardPlatformHealthdata}
          dashboardApiCactegoryUsagedata={dashboardApiCactegoryUsagedata}
          customers={customers}
          totalCustomersCount={totalCustomersCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
        />
      </Stack>
    </Wrapper>
  );
}